import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, CustomerOverview as OverviewData, KundeAkte, Target, Unternehmen } from "@kundeninfo/api-client";
import { Icon, useSofortPopup } from "@kundeninfo/ui";
import { Hinweis, Kachelwand } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../components/meHuellen.js";
import { UnternehmensChips } from "../components/Filterkopf.js";
import { useStabileListe } from "../components/stabileListe.js";
import { keepInView, scrollingAncestor, topTileInView } from "../scroll.js";
import type { AktenTabId } from "./KundenAkte.js";
import { ActionBar } from "./customerOverview/ActionBar.js";
import type { ActionBarState } from "./customerOverview/ActionBar.js";
import { HeaderCard } from "./customerOverview/HeaderCard.js";
import type { ContactState } from "./customerOverview/HeaderCard.js";
import { MonitorTile, useMonitorEntries } from "./customerOverview/MonitorTile.js";
import type { MonitorKartenClient } from "./customerOverview/MonitorTile.js";
import { TileView } from "./customerOverview/TileView.js";
import { TILE_CATALOG, TILE_MAPPINGS } from "./customerOverview/tileCatalog.js";
import { useTileLayout, visibleOrder } from "./customerOverview/tileLayout.js";
import { COV_TEXT, TILE_TEXT, companyOf, viewAnnouncement } from "./customerOverview/texts.js";
import "./customerOverview/CustomerOverview.css";

/*
 * Der Reiter „Übersicht“ der Kundenakte (371–373, 381–383): von oben nach unten Handlungsbedarf, Kopfkachel, die Leiste mit den
 * Unternehmens-Chips der Belegkacheln und die Kachelwand in der Anordnung des Benutzers. Ein Abruf liefert alles außer dem Monitor,
 * der für sich lädt.
 *
 * Bei jedem weiteren Abruf (Sichtwechsel, „Erneut laden“) bleibt der alte Stand stehen, bis der neue da ist; scheitert er, steht
 * nichts Altes mehr da (nie falsch grün). Eine Antwort, die von einer neueren überholt wurde, wird verworfen.
 *
 * Die Wand lässt sich anpassen (380, 384): Umordnen, Ausblenden und Zurücksetzen meldet sie selbst, `useTileLayout` hält die
 * Anordnung des Benutzers und speichert sie; die Wand folgt ihr, nicht der Antwort des Abrufs. Den Modus schaltet das Zahnrad
 * rechts in der Titelzeile des Handlungsbedarfs (im Modus derselbe Knopf als „Fertig“).
 */

export type OverviewClient = Pick<ApiClient, "getCustomerOverview" | "saveTileLayout" | "resetTileLayout">;

export type CustomerOverviewProps = {
  customer: KundeAkte;
  /** Alle Unternehmen des Benutzers: Kürzel, Name und Hausfarbe jeder Kennung. */
  companies: readonly Unternehmen[];
  /** Die Unternehmen mit einer Beziehung zu diesem Kunden — die Chips der Leiste. */
  customerCompanies: readonly Unternehmen[];
  activeCompanyId: string | null;
  /** Die zugeschalteten Unternehmen; ein Zustand mit den Belegreitern der Akte. */
  extraCompanyIds: readonly string[];
  onExtraCompanyIds: (ids: string[]) => void;
  /** Die Sicht der Belegkacheln als Abfrage (`sichtFuer`); `undefined` heißt: nur das aktive Unternehmen. */
  viewCompanyIds: readonly string[] | undefined;
  client: OverviewClient;
  monitorClient: MonitorKartenClient;
  onTab: (tab: AktenTabId) => void;
  /** Der Sprung einer Zeile mit ihrer Beschriftung; `nachladen` lädt die Übersicht im Stehen neu — nach einem Objektpanel, in
   *  dem geschrieben wurde (F9). */
  onTarget: (target: Target, label: string, nachladen: () => void) => void;
};

/** `reloading`: Nach einem Fehlschlag läuft ein neuer Abruf; der Fehler bleibt stehen, bis er antwortet. */
type State = { kind: "loading" } | { kind: "failed"; reloading?: true } | { kind: "ready"; data: OverviewData };

/**
 * Hält den obersten sichtbaren Block der Übersicht beim Neuzeichnen an seiner Stelle (Hausregel „Scroll bei Neuzeichnen“): Wächst
 * oder schrumpft etwas darüber, rollt der Bereich nach. Steht die Wand oben im Bild, ist es ihre oberste sichtbare Kachel (die
 * Wand selbst bleibt beim Umordnen, wo sie ist) — nie die bewegte `skipId`, die ihren Platz wechselt oder verschwindet. Ein Anker,
 * der dabei verschwindet (Warnung nach „Erneut laden“, ausgeblendete Kachel), wird nicht mehr gemessen (`keepInView`).
 */
/** Die Belegkacheln, für die die Unternehmenswahl gilt, in der Reihenfolge des Hinweises — nur die, die diese Akte zeigt. */
const CHIP_TILES = ["invoices", "billing", "offers", "orders", "supplierContracts", "incomingInvoices"] as const;
const documentTileTitles = (available: readonly string[]): string[] =>
  CHIP_TILES.filter((k) => available.includes(k) && TILE_CATALOG[k].documentBound).map((k) => TILE_TEXT[k].title);

function holdTopBlock(root: HTMLElement | null, skipId?: string): (() => void) | null {
  if (root === null) return null;
  const top = scrollingAncestor(root)?.getBoundingClientRect().top ?? 0;
  const inView = (element: Element): boolean => element.getBoundingClientRect().bottom > top;
  const block = [...root.children].find(inView);
  if (block === undefined) return null;
  const tile = block.localName === "me-kachelwand" ? topTileInView(block, skipId) : undefined;
  return keepInView(tile ?? block);
}

export function CustomerOverview(p: CustomerOverviewProps): ReactElement {
  const view = useStabileListe(p.viewCompanyIds);
  const [state, setState] = useState<State>({ kind: "loading" });
  // Jede Ansage bekommt eine neue Nummer und damit einen neuen Textknoten in der Region: So meldet sie auch denselben Satz noch
  // einmal (zweites „Erneut laden“; Muster TicketBoard `ansageNr`).
  const [announcement, setAnnouncement] = useState({ text: "", nr: 0 });
  const run = useRef(0);
  const previousView = useRef<string | null>(null);
  // Ein Sichtwechsel, dessen Antwort noch aussteht: Überholt ihn „Erneut laden“, sagt dessen Antwort die neue Sicht an.
  const viewPending = useRef(false);
  const root = useRef<HTMLDivElement | null>(null);
  const restore = useRef<(() => void) | null>(null);
  // Nur für die Ansage: Die Unternehmensliste darf den Abruf nicht neu anwerfen (sie entsteht im Aufrufer oft je Zeichnung neu).
  const companies = useRef(p.companies);
  useLayoutEffect(() => { companies.current = p.companies; });
  const { client, customer: { id: customerId } } = p;
  const [customizing, setCustomizing] = useState(false);
  const toggle = useRef<HTMLElement | null>(null);
  const focusToggle = useRef(false);
  // Das Zahnrad nennt seinen Namen im Sofort-Popup (Hausregel, kein `title`); im Modus trägt der Knopf den Text „Fertig“, dann schweigt es.
  const tip = useSofortPopup<HTMLElement>(customizing ? "" : COV_TEXT.customize);
  const toggleRef = useCallback((element: HTMLElement | null) => { toggle.current = element; tip(element); }, [tip]);
  // Merkt die Lage im Bild vor einer Änderung, die die Wand verschiebt (Anordnung, Modus, Meldung); der Layout-Effekt gleicht aus.
  const hold = (skipId?: string): void => { restore.current = holdTopBlock(root.current, skipId); };
  const tl = useTileLayout(client, state.kind === "ready" ? state.data.layout : null, hold);

  const load = useCallback(async (reason: "first" | "view" | "retry" | "changed"): Promise<void> => {
    const mine = ++run.current;
    if (reason === "view") viewPending.current = true;
    setState((s) => (s.kind === "failed" ? { kind: "failed", reloading: true } : s));
    let next: State;
    try {
      next = { kind: "ready", data: await client.getCustomerOverview(customerId, view) };
    } catch {
      next = { kind: "failed" };   // kein alter Stand: nie falsch grün
    }
    if (mine !== run.current) return;   // überholte Antwort
    restore.current = holdTopBlock(root.current);
    setState(next);   // der alte Stand blieb bis hierher stehen
    if (next.kind !== "ready") return;
    const text = viewPending.current
      ? viewAnnouncement(next.data.viewCompanyIds.map((id) => companyOf(id, companies.current).code))
      : reason === "retry" ? COV_TEXT.reloaded : null;
    viewPending.current = false;
    if (text !== null) setAnnouncement((a) => ({ text, nr: a.nr + 1 }));
  }, [client, customerId, view]);

  useEffect(() => {
    // Der Anlass folgt dem Wert der Sicht, nicht dem „ersten Lauf“: StrictMode ruft den Effekt doppelt.
    const key = JSON.stringify(view ?? null);
    const reason = previousView.current === null || previousView.current === key ? "first" : "view";
    previousView.current = key;
    void load(reason);
    return () => { run.current += 1; };   // nach dem Abbau schreibt keine Antwort mehr
  }, [load, view]);

  useLayoutEffect(() => {
    restore.current?.();
    restore.current = null;
  }, [state, tl.layout, tl.error, customizing]);

  // Der Hinweis „Alle Kacheln ausgeblendet“ schaltet den Modus ein und verschwindet dabei samt Knopf: Der Fokus geht an „Fertig“.
  useEffect(() => {
    if (customizing && focusToggle.current) toggle.current?.focus();
    focusToggle.current = false;
  }, [customizing]);

  const retry = (): void => { void load("retry"); };
  // Nach einem Objektpanel mit Änderung (F9, RF2): wie „Erneut laden“ ohne Ansage — der alte Stand bleibt bis zur Antwort.
  const changed = (): void => { void load("changed"); };
  const monitor = useMonitorEntries(p.monitorClient, customerId);
  const showMonitor = monitor.loadError !== null || (monitor.entries?.length ?? 0) > 0;
  const multiple = p.customer.relations.length > 1;

  const bar: ActionBarState = state.kind !== "ready" ? state : { kind: "ready", today: state.data.today,
    actionItems: state.data.actionItems, missingSources: state.data.missingSources };
  const contact: ContactState = state.kind === "loading" ? state
    : state.kind === "failed" || state.data.missingSources.includes("CONTACT") ? { kind: "failed" }
      : { kind: "ready", contact: state.data.mainContact };
  // Die Wand steht, wenn die Übersicht da ist; ihre Anordnung kommt aus `tl`, nicht aus der Antwort. Sie zeichnet nur, was diese Akte
  // freigibt (`availableTiles`, Lieferantenkacheln nur mit Lieferantenrolle und -recht) und was die Antwort als Abschnitt führt; einen
  // Schlüssel, den dieser Stand nicht kennt (Deploy-Versatz: alter Tab gegen neuen Server), zeichnet sie auch nicht. In der
  // gespeicherten Anordnung bleibt jeder stehen (`mergeLayout`).
  const ready = state.kind === "ready" && tl.layout !== null ? { data: state.data, layout: tl.layout,
    order: visibleOrder(tl.layout, state.data.availableTiles, state.data.tiles) } : null;
  const noteTiles = ready === null ? [] : documentTileTitles(ready.data.availableTiles);
  // Ob die Monitor-Kachel gezeigt wird, steht erst nach dem Laden fest; bis dahin gilt sie nicht als ausgeblendet (kein Flackern).
  const monitorKnown = monitor.entries !== null || monitor.loadError !== null;
  const allHidden = ready !== null && ready.order.every((key) =>
    ready.layout.hidden.includes(key) || (key === "monitor" && monitorKnown && !showMonitor));
  // „Nicht alle Kacheln …“ nur für Kacheln, die man sieht: Eine ausgeblendete, die nicht lädt, betrifft niemanden.
  const visibleFailed = ready !== null && ready.order.some((key) => key !== "monitor" && !ready.layout.hidden.includes(key)
    && ready.data.tiles[key]?.status === "ERROR");

  return (
    <div className="cov" ref={root}>
      <ActionBar state={bar} companies={p.companies} showCompanyCodes={multiple}
        onItem={(i) => p.onTarget(i.target, i.title === "" ? i.reference : i.title, changed)}
        onRetry={retry} headerAction={ready !== null && (
          // Ein Element für beide Zustände (der Fokus bleibt): Zahnrad mit dem Namen, im Modus der Text „Fertig“ ohne eigenen Namen.
          <KiKnopf className="cov__anpassen" kompakt knopfRef={toggleRef} onClick={() => { hold(); setCustomizing(!customizing); }}
            {...(customizing ? {} : { name: COV_TEXT.customize })}>
            {customizing ? COV_TEXT.done : <Icon name="gear" />}
          </KiKnopf>
        )} />
      <HeaderCard customer={p.customer} companies={p.companies} contact={contact} onTab={p.onTab} />
      {p.customerCompanies.length >= 2 && (
        <div className="cov__leiste">
          <UnternehmensChips label={COV_TEXT.chipsLabel} companies={p.customerCompanies} aktivId={p.activeCompanyId}
            zusatz={p.extraCompanyIds} onZusatz={p.onExtraCompanyIds} />
          {/* Ohne sichtbare Belegkachel kein Hinweis — kein hängendes „gilt für “ (Restkorrektur N3). */}
          {noteTiles.length > 0 && <span className="cov__leise">{COV_TEXT.chipsNote(noteTiles)}</span>}
        </div>
      )}
      {allHidden && !customizing && (
        <div className="cov__leer">
          <p>{COV_TEXT.allHidden}</p>
          <KiKnopf onClick={() => { hold(); focusToggle.current = true; setCustomizing(true); }}>{COV_TEXT.customize}</KiKnopf>
        </div>
      )}
      {visibleFailed && (
        <Hinweis art="warnung" aktionen={<KiKnopf onClick={retry}>{COV_TEXT.retry}</KiKnopf>}>{COV_TEXT.tilesFailed}</Hinweis>
      )}
      {ready !== null && tl.error !== null && (
        <Hinweis art="warnung">{tl.error === "reset" ? COV_TEXT.resetFailed : COV_TEXT.saveFailed}</Hinweis>
      )}
      {ready !== null && (
        <Kachelwand className="cov__wand" anpassen={customizing} onAnordnungGeaendert={tl.change} onZurueckgesetzt={tl.reset}>
          {ready.order.map((key) => {
            const hidden = ready.layout.hidden.includes(key);
            const onHead = (): void => p.onTab(TILE_CATALOG[key].tab);
            if (key === "monitor") {
              // `key` am Kunden: Die Kachel beginnt bei jedem Kunden neu (Review 11 M5).
              return showMonitor && <MonitorTile key={`monitor-${customerId}`} entries={monitor.entries ?? []}
                baseUrl={monitor.baseUrl} loadError={monitor.loadError} hidden={hidden} onHead={onHead} onRetry={monitor.reload} />;
            }
            // Kürzel nur bei mehreren Unternehmensbeziehungen, an Belegkacheln nur bei mehreren Unternehmen in der Sicht.
            const ctx = { today: ready.data.today, companies: p.companies,
              showCompanyCodes: multiple && (!TILE_CATALOG[key].documentBound || ready.data.viewCompanyIds.length > 1) };
            const section = ready.data.tiles[key];
            return section !== undefined && <TileView key={key} tileKey={key} section={section} mapping={TILE_MAPPINGS[key]} ctx={ctx}
              hidden={hidden} onHead={onHead} onRow={(r) => p.onTarget(r.target, r.reference ?? r.text, changed)} onRetry={retry} />;
          })}
        </Kachelwand>
      )}
      <p className="cov__unsichtbar" role="status" aria-live="polite"><span key={announcement.nr}>{announcement.text}</span></p>
    </div>
  );
}
